import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const groupKeys = {
  all: ['groups'],
  lists: () => [...groupKeys.all, 'list'],
  details: () => [...groupKeys.all, 'detail'],
  detail: (id) => [...groupKeys.details(), id],
};

export const useGroups = () => {
  return useQuery({
    queryKey: groupKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('groups');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useGroup = (id, options = {}) => {
  return useQuery({
    queryKey: groupKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`groups/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupData) => {
      const response = await apiClient.post('groups', groupData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating group:', error);
    }
  });
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`groups/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(groupKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating group:', error);
    }
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`groups/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.removeQueries({ queryKey: groupKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting group:', error);
    }
  });
};